import { useRef, useState } from 'react';
import { Button, Flex, Modal, notification, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useUserService } from '../../services/userService';
import { isImpersonationTab } from '../../services/impersonationSession';
import { notificationType } from '../../common/constants/notificationType';
import { spacing, textColor } from '../../theming';

const TITLE_ID = 'marketing-consent-prompt-title';
const BODY_ID = 'marketing-consent-prompt-body';

/**
 * The one-time marketing consent question (SC-QA-37), for a signed-in account that has never chosen
 * (currentUser.marketingChoiceNeeded). Yes and No look the same and either one closes it for good; there is no other
 * way to close it. Never shown in an impersonation tab: the admin must not answer for the user (the API refuses too).
 */
export default function MarketingConsentPrompt() {
    const { t } = useTranslation(['settings']);
    const [api, contextHolder] = notification.useNotification();
    const { currentUser, patchCurrentUser } = useAuth();
    const { answerMarketingConsentPrompt } = useUserService();

    // the answer being saved, or null
    const [saving, setSaving] = useState(null);
    // a failed save closes the prompt until the app loads again; the next /me asks again if nothing was stored
    const [closedForNow, setClosedForNow] = useState(false);
    // one save at a time, even if two clicks arrive before the re-render that disables the buttons
    const savingRef = useRef(false);

    const isOpen = currentUser?.marketingChoiceNeeded === true && !closedForNow && !isImpersonationTab();

    const answer = async value => {
        if (savingRef.current) {
            return;
        }
        savingRef.current = true;
        setSaving(value);

        try {
            const { data } = await answerMarketingConsentPrompt(value);
            // a 2xx means a choice is stored (this one, or one made elsewhere first): the stored value is shown
            patchCurrentUser({
                marketingChoiceNeeded: false,
                ...(typeof data?.marketingConsent === 'boolean' && { marketingConsent: data.marketingConsent }),
            });
        } catch (error) {
            setClosedForNow(true);
            // the interceptor stays silent on a 400; every other failure it has already shown
            if (error?.response?.status === 400) {
                api[notificationType.ERROR]({ message: t('settings:marketing-prompt.save-error'), placement: 'top' });
            }
        } finally {
            savingRef.current = false;
            setSaving(null);
        }
    };

    return (
        <>
            {contextHolder}
            <Modal
                open={isOpen}
                closable={false}
                maskClosable={false}
                keyboard={false}
                centered
                footer={null}
                aria-labelledby={TITLE_ID}
                aria-describedby={BODY_ID}
                // App.scss zeroes the padding of antd modals; add it back like the app's other modals
                styles={{ body: { padding: `${spacing[5]} ${spacing[4]}` } }}
            >
                <Flex vertical gap={spacing[3]}>
                    <Typography.Title level={4} id={TITLE_ID} style={{ margin: 0 }}>
                        {t('settings:marketing-prompt.title')}
                    </Typography.Title>
                    <Flex vertical gap={spacing[1]} id={BODY_ID}>
                        <Typography.Text>{t('settings:marketing-prompt.body')}</Typography.Text>
                        <Typography.Text style={{ color: textColor.secondary }}>
                            {t('settings:marketing-prompt.hint')}
                        </Typography.Text>
                    </Flex>
                    {/* the same style for both answers: saying no is as easy as saying yes */}
                    <Flex gap={spacing[2]} justify="end" wrap>
                        <Button onClick={() => answer(false)} loading={saving === false} disabled={saving !== null}>
                            {t('settings:marketing-prompt.decline')}
                        </Button>
                        <Button onClick={() => answer(true)} loading={saving === true} disabled={saving !== null}>
                            {t('settings:marketing-prompt.accept')}
                        </Button>
                    </Flex>
                </Flex>
            </Modal>
        </>
    );
}
